import { array, bool, number, string, func, object } from 'prop-types'
import { Component, createRef } from 'react'
import { hideAll as tippyHideAll } from 'tippy.js'
import { v4 as uuidv4 } from 'uuid'
import {
  Alert,
  Button,
  Conditional,
  PillV2,
  PopoverV2,
  Loader,
  kuiperClassNames,
} from '@opallabs/kuiper'
import { getNestedProperty } from '@opallabs/lambda'
import { AnalyticsConsumer } from '@opallabs/quanta'
import AnnotationMessage from '../AnnotationMessage'
import './styles.scss'
import { Placemark } from './Placemark'

const alertTitle = 'Are you sure want to delete this?'
const alertMessage =
  'Deleting this will permanently remove this annotation from this content. ' +
  'Other users may still see this annotation in historical activity and notifications.'
export const triggerIconOffset = 10

const getNestedMessageProperty = (messages, ...path) => {
  if (!messages.length) {
    return ''
  }

  return getNestedProperty(messages[0], ...path)
}

const simplifyUserObject = (user) => ({
  id: user.id,
  name: user.fullName,
  avatar: user.avatar,
})

const sortByUserName = (a, b) => {
  if (a.name > b.name) return 1
  if (a.name < b.name) return -1
  return 0
}

const getUserName = (annotation, users) => {
  const userId = getNestedProperty(annotation, 'user', 'id')
  if (!userId || !users.length) return null

  const user = users.find((u) => u.id === userId)
  return user.fullName
}

function forceSafariRepaint(targetElement) {
  if (!targetElement) return

  // using setTimeout without a timeout value to push these style changes to as late
  // as possible in the task queue, but without introducing any other extra delay
  // without it safari seems to elide right over the display: none
  setTimeout(() => {
    targetElement.style.display = 'none'
    targetElement.style.display = 'initial'
  })
}

export default class AnnotationEditor extends Component {
  static propTypes = {
    annotations: array,
    createAnnotation: func,
    createMessage: func,
    currentUser: object,
    deleteAnnotation: func,
    deleteMessage: func,
    getAnnotations: func,
    getCurrentUser: func,
    // Existing pins and their comments render as usual, but nothing can be
    // added or removed.
    readOnly: bool,
    showAnnotation: string,
    snapshot: string,
    snapshotId: number,
    users: array,
    userTimezone: string,
  }

  static defaultProps = {
    annotations: [],
    readOnly: false,
    users: [],
  }

  constructor(props) {
    super(props)

    this.state = {
      activeAnnotation: null,
      forceClosePopover: false,
      imageWidth: '100%',
      popoverShouldClose: true,
      shouldAdd: true,
      showAlert: false,
      snapshotLoaded: false,
      unsavedAnnotations: [],
    }

    this.imageRef = createRef()
    this.annotationsContainer = createRef()
  }

  componentDidMount() {
    const { currentUser, getAnnotations, getCurrentUser, snapshotId } =
      this.props
    if (!currentUser) {
      getCurrentUser()
    }
    getAnnotations({ snapshotId })

    // Wait to open the initial annotation popover so the user notices it
    if (!this.props.showAnnotation) return
    setTimeout(() => {
      this.setState({ showAnnotation: this.props.showAnnotation })
    }, 750)
  }

  componentDidUpdate(prevProps) {
    const { getAnnotations, snapshot, snapshotId } = this.props
    if (prevProps.snapshot === undefined && snapshot !== undefined) {
      this.setState({ imageWidth: '100%' })
    }

    if (prevProps.snapshotId !== snapshotId) {
      getAnnotations({ snapshotId })
    }
  }

  onImageLoad = () => {
    const imageWidth = this.imageRef.current.naturalWidth
    this.setState({ snapshotLoaded: true, imageWidth: imageWidth / 2 })
  }

  addAnnotation = (e, track) => {
    if (!this.state.shouldAdd) {
      return track('click', 'Cancel')
    }

    const location = {
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY,
    }
    const newAnnotation = {
      id: uuidv4(),
      location,
      isNew: true,
      messages: [],
      user: this.props.currentUser,
    }

    track('click', newAnnotation)

    return this.setState(() => ({
      unsavedAnnotations: [newAnnotation],
      shouldAdd: false,
    }))
  }

  deleteAnnotation = () => {
    const { annotations, deleteMessage, deleteAnnotation } = this.props
    const annotationToDelete = annotations.find(
      (annotation) => annotation.id === this.state.deleteableAnnotation
    )
    return deleteMessage({ messageId: annotationToDelete.messages[0].id }).then(
      () => {
        deleteAnnotation({ annotationId: this.state.deleteableAnnotation })
        return this.toggleAlert(null)
      }
    )
  }

  submitAnnotation = (messageText) => {
    const { snapshotId, createAnnotation } = this.props
    const unsavedAnnotation = this.state.unsavedAnnotations[0]
    this.setState({ popoverShouldClose: true })
    return createAnnotation({
      snapshotId,
      location: JSON.stringify(unsavedAnnotation.location),
      creatorGuid: unsavedAnnotation.id,
    }).then((response) => {
      this.setState({ unsavedAnnotations: [] })
      return this.createMessage(response.id, messageText, unsavedAnnotation.id)
    })
  }

  createMessage = (id, messageText, creatorGuid) => {
    const { createMessage } = this.props
    return createMessage({
      annotationId: id,
      message: messageText,
      creatorGuid,
    }).then(() => this.setState({ showAnnotation: id, shouldAdd: true }))
  }

  resetUnsavedAnnotations = () => {
    // draftjs prevents popover from destroying/unmounting
    // so we force click of document and remove all poppers to ensure it removes
    document.body.click()
    tippyHideAll()
    this.setState({
      unsavedAnnotations: [],
      shouldAdd: true,
      popoverShouldClose: true,
    })
  }

  onPopoverOpen = (annotation) => {
    this.setState({ activeAnnotation: annotation.id })
    if (!annotation.isNew) this.setState({ shouldAdd: false })
  }

  onPopoverClosed = () => {
    this.setState({ activeAnnotation: null })
    if (this.state.popoverShouldClose) {
      this.resetUnsavedAnnotations()
    }

    this.setState({ shouldAdd: true, showAnnotation: null })
  }

  toggleAlert = (deleteableAnnotation, showPreviousAnnotation) =>
    this.setState((prevState) => ({
      showAlert: !prevState.showAlert,
      forceClosePopover: !prevState.forceClosePopover,
      deleteableAnnotation,
      showAnnotation: showPreviousAnnotation
        ? prevState.deleteableAnnotation
        : null,
    }))

  viewUserProfile = (id) => {
    const userAttributes = this.props.users.find((user) => user.id === id)
    const backboneModel =
      window.AppBase.Entities.User.findOrCreate(userAttributes)
    window.AppBase.execute('user_profile:show', { model: backboneModel })
  }

  closePopover = () => {
    this.state.popoverShouldClose
    forceSafariRepaint(this.annotationsContainer.current)
  }

  onCancelClick = () => {
    this.closePopover()
    this.onPopoverClosed()
    this.resetUnsavedAnnotations()
  }

  render() {
    const { currentUser, readOnly, snapshot, userTimezone, users } = this.props
    const annotations = [
      ...this.props.annotations,
      ...this.state.unsavedAnnotations,
    ]

    return (
      <div
        className={kuiperClassNames('annotations-editor', {
          'alert-open': this.state.showAlert,
        })}
      >
        <div
          className={kuiperClassNames('snapshot-content', {
            crosshair: this.state.shouldAdd && !readOnly,
          })}
          style={{
            inlineSize: this.state.snapshotLoaded && this.state.imageWidth,
          }}
        >
          <AnalyticsConsumer>
            {({ track }) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <img
                ref={this.imageRef}
                src={snapshot}
                onLoad={this.onImageLoad}
                alt="snapshot"
                onClick={
                  readOnly ? undefined : (e) => this.addAnnotation(e, track)
                }
              />
            )}
          </AnalyticsConsumer>
          <Conditional condition={this.state.snapshotLoaded}>
            <div ref={this.annotationsContainer}>
              {annotations.map((annotation) => (
                <div
                  key={annotation.id}
                  className={kuiperClassNames('annotation')}
                  style={{
                    insetInlineStart: `${annotation.location.x - triggerIconOffset}px`,
                    insetBlockStart: `${annotation.location.y - triggerIconOffset}px`,
                  }}
                >
                  <PopoverV2
                    className="annotations-message"
                    disabled={!this.state.popoverShouldClose}
                    forceClose={this.state.forceClosePopover}
                    onClose={this.closePopover}
                    onClosed={this.onPopoverClosed}
                    onOpen={() =>
                      this.setState({ activeAnnotation: annotation.id })
                    }
                    onOpened={() => this.onPopoverOpen(annotation)}
                    open={
                      this.state.showAnnotation === annotation.id && !!snapshot
                    }
                    showOnCreate={annotation.isNew}
                    trigger={
                      <Placemark
                        active={this.state.activeAnnotation === annotation.id}
                      />
                    }
                    triggerEvent={annotation.isNew ? 'click' : 'mouseenter'}
                    content={
                      <AnnotationMessage
                        createdAt={getNestedMessageProperty(
                          annotation.messages,
                          'created_at'
                        )}
                        editing={annotation.isNew}
                        message={getNestedMessageProperty(
                          annotation.messages,
                          'message'
                        )}
                        mentionableUsers={users
                          .map(simplifyUserObject)
                          .sort(sortByUserName)}
                        isCurrentUser={
                          getNestedMessageProperty(
                            annotation.messages,
                            'user',
                            'id'
                          ) === currentUser.id
                        }
                        popoverShouldClose={this.state.popoverShouldClose}
                        readOnly={readOnly}
                        togglePopoverShouldClose={(value) =>
                          this.setState({ popoverShouldClose: value })
                        }
                        onCancel={this.onCancelClick}
                        onDelete={() => this.toggleAlert(annotation.id)}
                        onSubmit={(messageText) =>
                          this.submitAnnotation(messageText)
                        }
                        userId={getNestedProperty(annotation, 'user', 'id')}
                        userName={getUserName(annotation, users)}
                        userTimezone={userTimezone}
                        viewUserProfile={this.viewUserProfile}
                      />
                    }
                  />
                </div>
              ))}
            </div>
          </Conditional>
          <Loader loaded={this.state.snapshotLoaded} theme="n1" />
        </div>

        <Conditional condition={!readOnly}>
          <div className={kuiperClassNames('instructions')}>
            <PillV2
              content="Annotate"
              icon="annotate"
              bgColor="blue"
              fgColor="blue-darkest"
              size="large"
            />
            <PillV2
              content="Click anywhere on content"
              bgColor="blue-dark"
              fgColor="blue-darkest"
              size="large"
            />
          </div>
        </Conditional>
        <Conditional condition={this.state.showAlert}>
          <div className={kuiperClassNames('delete-alert-wrapper')}>
            <Button
              className="close-delete-alert"
              label="Cancel"
              type="nobutton"
              icon="close-sm"
              size="medium"
              onClick={() => this.toggleAlert(null, true)}
            />
            <Alert message={alertMessage} title={alertTitle}>
              <Button
                className="cancel-button"
                label="Cancel"
                type="nobutton"
                onClick={() => this.toggleAlert(null, true)}
              />
              <Button
                className="delete-button"
                color="red"
                label="Delete Annotation"
                onClick={this.deleteAnnotation}
              />
            </Alert>
          </div>
        </Conditional>
      </div>
    )
  }
}
